import type { ReactNode } from "react"
import { type DeclaredMetric, formatValue, toneOf } from "@/components/Declared.js"
import { Explained } from "@/components/ui/tooltip.js"
import { cn } from "@/lib/utils.js"

/**
 * How a number is put in front of somebody here: **label, value, nothing
 * else.**
 *
 * Two earlier versions of this screen failed in opposite directions. Ten
 * equal-sized figures in a row was a wall nobody could read. Printing every
 * explanation under every figure was the same wall in prose — fifteen
 * sentences per source, and the numbers disappeared between them. What is on
 * the screen is the data; the sentence behind a number is one hover away,
 * marked by the dotted rule under its label.
 */

const TONES = {
  neutral: "text-foreground",
  ember: "text-primary",
  allow: "text-allow",
  critical: "text-critical",
} as const

/** The one or two figures a source's grade turns on. Beside its name. */
export function Figures({ facts }: { facts: readonly DeclaredMetric[] }) {
  if (facts.length === 0) return null

  /*
   * A grid rather than a row: five or six figures in one line either overflow
   * or wrap where the width happens to run out, and the card ends up with a
   * different shape per source. Three columns hold what a source leads with
   * and keep the right half of the card carrying data.
   */
  return (
    <div className="grid min-w-[17rem] shrink-0 grid-cols-2 gap-x-8 gap-y-3.5 sm:grid-cols-3">
      {facts.map((fact) => (
        <div key={fact.key}>
          <p className="text-[12.5px] text-muted-foreground">
            <Explained hint={fact.hint}>{fact.label}</Explained>
          </p>
          <p
            className={cn(
              "mt-0.5 text-[17px] font-semibold leading-6 tracking-tight tabular-nums",
              TONES[toneOf(fact.tone)],
            )}
          >
            {formatValue(fact)}
          </p>
        </div>
      ))}
    </div>
  )
}

/** Everything else about a source: two columns of label and value. */
export function FactGrid({ facts }: { facts: readonly DeclaredMetric[] }) {
  if (facts.length === 0) return null

  return (
    <dl className="grid gap-x-10 sm:grid-cols-2">
      {facts.map((fact) => (
        <div
          key={fact.key}
          className="flex items-baseline justify-between gap-4 border-b py-1.5 last:border-b-0"
        >
          <dt className="min-w-0 text-[13px] text-muted-foreground">
            <Explained hint={fact.hint}>{fact.label}</Explained>
          </dt>
          <dd className={cn("shrink-0 text-sm font-medium tabular-nums", TONES[toneOf(fact.tone)])}>
            {formatValue(fact)}
          </dd>
        </div>
      ))}
    </dl>
  )
}

/**
 * A run of small figures on one line — the traffic the core counted, which is
 * seven numbers that belong together and none of which is worth a row.
 */
export function Strip({ facts }: { facts: readonly DeclaredMetric[] }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1.5 text-[13px]">
      {facts.map((fact) => (
        <span key={fact.key} className="inline-flex items-baseline gap-1.5">
          <span className="text-muted-foreground">
            <Explained hint={fact.hint}>{fact.label}</Explained>
          </span>
          <span className={cn("font-medium tabular-nums", TONES[toneOf(fact.tone)])}>
            {formatValue(fact)}
          </span>
        </span>
      ))}
    </div>
  )
}

/** A heading inside a source's card. No caption: the block is what it says. */
export function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-2">
      <h4 className="text-[13px] font-semibold text-foreground">{title}</h4>
      {children}
    </section>
  )
}
